import React, { FC } from "react";
import { Link } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import OverflowTooltip from "../../../../components/OverflowTooltip/OverflowTooltip";
import { ReactComponent as PhoneNumber } from "../../../../assets/svg/phone-number.svg";
import { ReactComponent as Website } from "../../../../assets/svg/website.svg";
import { ReactComponent as Email } from "../../../../assets/svg/email.svg";
import { urlNormalizer } from "../../../../helpers";
import SchoolDefaultLogo from "../../../../assets/img/school-default-logo.png";
import Masker from "../../../../components/Masker/Masker";

const useStyles = makeStyles(() => ({
  header: {
    padding: "24px 32px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  schoolInfoMainContainer: {
    display: "flex",
  },
  schoolImageContainer: {
    margin: "auto 0px",
  },
  schoolImage: {
    backgroundSize: "100% auto",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  },
  schoolInfoContainer: {
    marginLeft: 32,
    maxWidth: 714,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  row0: {
    fontSize: 25,
    fontWeight: "bold",
    color: "#231F20",
    marginTop: 7,
    lineHeight: 1.1,
  },
  row1: {
    fontSize: 20,
    color: "#231F20",
    marginTop: 12,
    display: "flex",
  },
  row1text1: {
    marginLeft: 16,
    fontWeight: "bold",
    maxWidth: 588,
  },
  row2: {
    fontSize: 16,
    color: "#231F20",
    marginTop: 12,
  },
  row2SubText1: {
    fontWeight: 500,
  },
  row3: {
    fontSize: 16,
    color: "#231F20",
    marginTop: 12,
  },
  row3SubText0: {
    fontWeight: 500,
  },
  contactIconContainer: {
    display: "flex",
    alignItems: "center",
    "& + &": {
      marginTop: 12,
    },
  },
  title: {
    fontSize: 16,
    color: "#231F20",
    fontWeight: "bold",
    marginLeft: 8,
    maxWidth: 200,
  },
  link: {
    "&:hover": {
      color: "#858997",
    },
  },
}));

interface SchoolInfoShowProps {
  school: {
    name: string;
    image?: string;
    defaultImage?: string;
    country: string;
    email: string;
    phone: string;
    website: string;
  };
  isMasked?: boolean;
  row0: {
    text0: string;
  };
  row1?: {
    text0?: string;
    text1?: string;
  };
  row2?: {
    text0?: string;
    text1?: string;
    subText1?: string;
  };
  row3?: {
    text0?: string;
    subText0?: string;
  };
}

export const SchoolInfoWidget: FC<SchoolInfoShowProps> = ({
  school: { image, defaultImage = SchoolDefaultLogo, email, phone, website },
  isMasked,
  row0,
  row1,
  row2,
  row3,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.header}>
      <div className={classes.schoolInfoMainContainer}>
        <div className={classes.schoolImageContainer}>
          <img
            alt={"School Logo"}
            src={image ?? defaultImage}
            className={classes.schoolImage}
            width={74}
            height={74}
          />
        </div>
        <div className={classes.schoolInfoContainer}>
          <div className={classes.row0}>
            <OverflowTooltip text={row0.text0} />
          </div>
          {!!row1 && (
            <div className={classes.row1}>
              {!!row1.text0 && <OverflowTooltip text={row1.text0} />}
              {!!row1.text1 && (
                <div className={classes.row1text1}>
                  <OverflowTooltip text={row1.text1} />
                </div>
              )}
            </div>
          )}
          {!!row2 && (
            <div className={classes.row2}>
              {!!row2.text0 && <OverflowTooltip text={row2.text0} />}
              {!!row2.text1 && (
                <>
                  {row2.text1}
                  {!!row2.subText1 && (
                    <span className={classes.row2SubText1}>
                      {row2.subText1}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          {!!row3 && (
            <div className={classes.row3}>
              {!!row3.text0 && (
                <>
                  {row3.text0}
                  {!!row3.subText0 && (
                    <span className={classes.row3SubText0}>
                      {row3.subText0}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <div className={classes.contactIconContainer}>
          <PhoneNumber />
          <div className={classes.title}>
            {isMasked ? (
              <Masker isMasked={isMasked} children={phone} />
            ) : (
              <Link
                className={classes.link}
                href={"tel:" + phone}
                underline="none"
              >
                {!!phone && <OverflowTooltip text={phone} />}
              </Link>
            )}
          </div>
        </div>
        <div className={classes.contactIconContainer}>
          <Email />
          <div className={classes.title}>
            {isMasked ? (
              <Masker isMasked={isMasked} children={email} />
            ) : (
              <Link
                className={classes.link}
                href={"mailto:" + email}
                underline="none"
              >
                <OverflowTooltip text={email} />
              </Link>
            )}
          </div>
        </div>
        <div className={classes.contactIconContainer}>
          <Website />
          <div className={classes.title}>
            {isMasked ? (
              <Masker isMasked={isMasked} children={website} />
            ) : (
              <Link
                className={classes.link}
                href={urlNormalizer(website)}
                target="_blank"
                underline="none"
              >
                {!!website && <OverflowTooltip text={website} />}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
